<div>
    <h2 id="page-heading" class="running-page-heading">
        <span class="d-flex align-items-center gap-2">
            <span>Running</span>
            <span class="running-count" *ngIf="executions.length">
                <span class="running-dot"></span>
                {{ executions.length }} in flight
            </span>
        </span>
        <span class="running-hint">everything that is not in a final status &middot; refreshes every second</span>
    </h2>
    <app-alert></app-alert>

    <div class="table-responsive" *ngIf="executions.length">
        <table class="table table-striped align-middle text-center">
            <thead>
            <tr>
                <th class="col-acct-code text-start">Account</th>
                <th class="col-phase">Phase</th>
                <th class="col-runner">Runner</th>
                <th class="col-status">Status</th>
                <th class="text-start">Message</th>
                <th class="col-ping">Last ping</th>
                <th class="col-time" title="From the refresh request until the runner picked it up">Queue</th>
                <th class="col-time" title="Runner work time, waiting for the user excluded">Execution</th>
                <th class="col-time" title="Time spent waiting for the user to answer a form">Waiting</th>
            </tr>
            </thead>
            <tbody>
            <tr *ngFor="let row of executions; trackBy: trackByExecution">
                <td class="col-acct-code text-start">
                    <span class="col-mono">{{ shortAccountCode(row.accountCode) }}</span>
                    <button class="btn btn-sm btn-outline-secondary ms-2"
                            title="Copy account code"
                            (click)="copyToClipboard(row.accountCode)">
                        <i class="fa fa-copy"></i>
                    </button>
                    <small class="acct-extractor" *ngIf="row.extractorCode">{{ row.extractorCode }}</small>
                </td>
                <td class="col-phase">
                    <span class="ph-badge" [ngClass]="phaseClass(row.phase)" [title]="phaseHint(row.phase)">
                        {{ phaseLabel(row.phase) }}
                    </span>
                </td>
                <td class="col-runner col-mono">{{ row.runnerCode || (row.runnerId ? 'Id: ' + row.runnerId : '—') }}</td>
                <td class="col-status col-mono">{{ row.internalStatus }}</td>
                <td class="text-start running-message">{{ row.internalMessage }}</td>
                <td class="col-ping" [class.wd-stale]="isStale(row)">
                    <span class="ping-dot" *ngIf="row.alive" [class.ping-dot-stale]="isStale(row)"></span>
                    {{ pingText(row) }}
                </td>
                <td class="col-time col-mono"
                    [title]="timingTitle(row.inFlightSeconds, row.queuedSeconds, row.waitingSeconds, row.executionSeconds)">
                    {{ timeText(row.queuedSeconds) }}
                </td>
                <td class="col-time col-mono"
                    [title]="timingTitle(row.inFlightSeconds, row.queuedSeconds, row.waitingSeconds, row.executionSeconds)">
                    {{ timeText(row.executionSeconds) }}
                </td>
                <td class="col-time col-mono" [class.wait-active]="row.phase === 'WAITING_USER'"
                    [title]="timingTitle(row.inFlightSeconds, row.queuedSeconds, row.waitingSeconds, row.executionSeconds)">
                    {{ timeText(row.waitingSeconds) }}
                </td>
            </tr>
            </tbody>
        </table>
    </div>

    <div class="empty-state running-empty-state" *ngIf="!executions.length">
        <i class="fa fa-moon-o"></i>
        <span>Nothing is in flight right now.</span>
    </div>

    <div class="wd-section">
        <div class="wd-head">
            <span class="wd-title">Refresh queue</span>
            <span class="wd-count" *ngIf="queue.length">{{ queue.length }}</span>
            <span class="wd-sub">unprocessed rows waiting for a runner slot</span>
        </div>

        <div class="wd-empty" *ngIf="!queue.length">The queue is empty.</div>

        <div class="table-responsive scroll-10" *ngIf="queue.length">
            <table class="table table-sm wd-table align-middle text-center">
                <thead>
                <tr>
                    <th class="text-start">Account</th>
                    <th class="text-start">Extractor</th>
                    <th class="col-source">Source</th>
                    <th class="col-priority">Priority</th>
                    <th class="col-runner">Runner</th>
                    <th class="col-time">Waiting</th>
                </tr>
                </thead>
                <tbody>
                <tr *ngFor="let row of queue; trackBy: trackByQueueRow">
                    <td class="text-start col-mono">{{ shortAccountCode(row.accountCode) }}</td>
                    <td class="text-start col-mono">{{ row.extractorCode || '—' }}</td>
                    <td class="col-source">
                        <span class="src-badge" [ngClass]="sourceClass(row)">{{ sourceLabel(row) }}</span>
                    </td>
                    <td class="col-priority col-mono">{{ row.priority }}</td>
                    <td class="col-runner col-mono">{{ row.runnerId ? 'Id: ' + row.runnerId : '—' }}</td>
                    <td class="col-time col-mono">{{ formatDuration(row.waitingSeconds) }}</td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="wd-section">
        <div class="wd-head">
            <span class="wd-title">Recently finished</span>
            <span class="wd-count" *ngIf="recent.length">{{ recent.length }}</span>
            <span class="wd-sub">last 24 hours, newest first</span>
        </div>

        <div class="wd-empty" *ngIf="!recent.length">Nothing finished in the last 24 hours.</div>

        <div class="table-responsive scroll-10" *ngIf="recent.length">
            <table class="table table-sm wd-table align-middle text-center">
                <thead>
                <tr>
                    <th class="text-start">Account</th>
                    <th class="col-outcome">Outcome</th>
                    <th class="col-status">Status</th>
                    <th class="text-start">Message</th>
                    <th class="col-runner">Runner</th>
                    <th class="col-time" title="Runner work time, queue and waiting for the user excluded">Took</th>
                    <th class="col-time" title="Time spent waiting for the user to answer a form">Waited</th>
                    <th class="col-time">Finished</th>
                </tr>
                </thead>
                <tbody>
                <tr *ngFor="let row of recent; trackBy: trackByRecent">
                    <td class="text-start col-mono">
                        {{ shortAccountCode(row.accountCode) }}
                        <button class="btn btn-sm btn-outline-secondary ms-2"
                                title="Copy account code"
                                (click)="copyToClipboard(row.accountCode)">
                            <i class="fa fa-copy"></i>
                        </button>
                    </td>
                    <td class="col-outcome">
                        <span class="ph-badge" [ngClass]="outcomeClass(row)">{{ outcomeLabel(row) }}</span>
                    </td>
                    <td class="col-status col-mono">{{ row.internalStatus }}</td>
                    <td class="text-start running-message">{{ row.internalMessage }}</td>
                    <td class="col-runner col-mono">{{ row.runnerCode || '—' }}</td>
                    <td class="col-time col-mono"
                        [title]="timingTitle(row.durationSeconds, row.queuedSeconds, row.waitingSeconds, row.executionSeconds)">
                        {{ timeText(row.executionSeconds) }}
                    </td>
                    <td class="col-time col-mono"
                        [title]="timingTitle(row.durationSeconds, row.queuedSeconds, row.waitingSeconds, row.executionSeconds)">
                        {{ timeText(row.waitingSeconds) }}
                    </td>
                    <td class="col-time col-mono">{{ finishedText(row) }}</td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>

</div>
